import { Injectable } from '@angular/core';
import { IJsonApiResponse, IMap } from '@reg/shared/json-api/json-api.interface';
import { EventCore, ExternalAssociation } from '@reg/shared/model';
import { Observable, lastValueFrom, map } from 'rxjs';
import { HttpServiceHelper } from '../http-service-helper';

@Injectable()
export class EventCoreService {
    get siteId(): number {
        return this.http.configService.getSportConfig('siteId');
    }

    constructor(private http: HttpServiceHelper) {
    }

    private appendGetCoreFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.lastIndexOf('fields') !== -1).length === 0) {
            queryParams.push(`fields=${EventCore.getFields().join(',')}`);
        }

        return queryParams;
    }

    /**
     * get events from core api
     */
    public getEventsFromCore(
        queryParams: string[] = null,
    ): Observable<EventCore[]> {
        queryParams = queryParams.concat([`site_id=${this.siteId}`, 'limit=500']);
        queryParams = this.appendGetCoreFields(queryParams);

        const apiRequestUrl: string = this.getCoreApiSearchUrl(queryParams);

        return this.http.get(apiRequestUrl, true)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;
                const events: EventCore[] = json.data?.map((d: any) => new EventCore(this.siteId).deserialize(d));
                return this.filterCoreEvents(events);
            }));
    }

    /**
     * Build the core api search url based on the given parameters
     */
    private getCoreApiSearchUrl(queryParams: string[] = null): string {
        let url: string = `${this.http.configService.getCoreApiBaseUrl()}/events/search`;

        url += this.http.getQueryParameters(null, queryParams);

        return url;
    }

    // filter out the events that don't have a salesforce link
    private filterCoreEvents(events: EventCore[]): EventCore[] {
        if (!events?.length) return [];
        return events.filter((event: EventCore) => event.hasSalesforceLink);
    }

    // create post call to set the external association for the core event
    public saveExternalAssociation(eventId: string, externalAssociation: ExternalAssociation): Observable<EventCore> {
        const apiRequestUrl: string = this.getCoreApiExternalAssociationUrl(eventId);
        return this.http.post(apiRequestUrl, externalAssociation.serialize())
            .pipe(
                map((response: IJsonApiResponse) => new EventCore(this.siteId).deserialize(response)),
            );
    }

    public async cleanUpExternalAssociations(arenaLinkedIds: string[]): Promise<void> {
        const externalAssociations: ExternalAssociation[] =
            await this.getCoreEventsExternalAssociations(arenaLinkedIds);
        if (!externalAssociations?.length) return;

        // get core events ids
        const coreEventIds: string[] = externalAssociations
            .map((externalAssociation: ExternalAssociation) => externalAssociation.externalId);
        const coreEvents: EventCore[] = await this.getCoreEventsListFromIds(coreEventIds);
        if (!coreEvents?.length) return;

        // get core events that don't have a salesforce link
        const eventsWithoutSalesforce: EventCore[] =
            coreEvents.filter((coreEvent: EventCore) => !coreEvent.hasSalesforceLink);
        // create a map for external associations for easy matching and removal
        const externalAssociationMap: IMap<ExternalAssociation> = {};
        externalAssociations.forEach((externalAssociation: ExternalAssociation) => {
            const arenaExternalAssociation: ExternalAssociation = new ExternalAssociation();
            // set the external association properties with arenaEventId
            arenaExternalAssociation.externalId = externalAssociation.arenaEventId;
            arenaExternalAssociation.externalSource = externalAssociation.externalSource;
            arenaExternalAssociation.externalType = externalAssociation.externalType;
            externalAssociationMap[externalAssociation.externalId] = arenaExternalAssociation;
        });

        if (!eventsWithoutSalesforce?.length) return;

        // remove external association that doesn't have a salesforce link
        const deleteCallList: Promise<void>[] = eventsWithoutSalesforce.map((coreEvent: EventCore) => {
            if (!externalAssociationMap[coreEvent.id]) return;
            return this.removeExternalAssociation(coreEvent, externalAssociationMap[coreEvent.id]);
        });

        await Promise.all([...deleteCallList]);
    }

    private async removeExternalAssociation(coreEvent: EventCore, externalAssociation: ExternalAssociation)
        : Promise<void> {
        const apiRequestUrl: string = this.getCoreApiExternalAssociationUrl(coreEvent.id);

        const result: Observable<EventCore> = this.http.delete(
            apiRequestUrl,
            false,
            true,
            this.http.getHeadersForJson(),
            externalAssociation.serialize(),
        ).pipe(
            map((response: IJsonDeleteExtAssociationResponse) => {
                const data: any = response.data;
                if (data.status) {
                    coreEvent.externalAssociations = coreEvent.externalAssociations
                        .filter((ea: ExternalAssociation) => ea.externalId !== externalAssociation.externalId);
                    return coreEvent;
                }
            }),
        );

        // if the event has no external associations, set it to draft
        result.subscribe(async (eventCore) => {
            if (eventCore.externalAssociations?.length === 0) await this.setToDraft(eventCore.id);
        });
    }

    public async setToDraft(eventCoreId: string): Promise<void> {
        const apiRequestUrl: string = this.getCoreApiSetToDraftUrl(eventCoreId);
        const status: any = { status: 0 };
        const result: Observable<void> = this.http.patch(apiRequestUrl, JSON.stringify(status), false);
        return lastValueFrom(result);
    }

    public async getCoreEventsListFromIds(eventIds: string[]): Promise<EventCore[]> {
        const apiRequestUrl: string = this.getCoreApiEventFromIdsUrl(eventIds);
        const result: Observable<EventCore[]> = this.http.get(apiRequestUrl, false)
            .pipe(
                map((response: IJsonApiResponse) => {
                    const json: any = response;
                    return json.data?.map((d: any) => new EventCore(this.siteId).deserialize(d));
                }),
            );
        return lastValueFrom(result);
    }

    private async getCoreEventsExternalAssociations(arenaLinkedIds: string[]): Promise<ExternalAssociation[]> {
        const apiRequestUrl: string = this.getCoreEventsLinkedToArenaUrl();
        const body = {
            ids: arenaLinkedIds,
        };
        const result: Observable<ExternalAssociation[]> = this.http.post(apiRequestUrl, JSON.stringify(body), false)
            .pipe(
                map((response: IJsonApiResponse) => {
                    const json: any = response;
                    if (json.data && Object.values(json.data).length > 0) {
                        const result: ExternalAssociation[] = Object.keys(json.data)
                            .map((key: string) => {
                                const newExternalAssociation = new ExternalAssociation().deserialize(json.data[key]);
                                newExternalAssociation.arenaEventId = key;
                                return newExternalAssociation;
                            });
                        return result;
                    }
                    return [];
                }),
            );
        return lastValueFrom(result);
    }

    private getCoreApiSetToDraftUrl(eventId: string): string {
        return `${this.http.configService.getCoreApiBaseUrl()}/events/${eventId}`;
    }

    private getCoreApiEventFromIdsUrl(eventIds: string[]): string {
        const idParameter: string = `?ids=${eventIds.join(',')}`;
        const url: string = `${this.http.configService.getCoreApiBaseUrl()}/nodes/list/from-ids`;
        return url + idParameter;
    }

    private getCoreEventsLinkedToArenaUrl(): string {
        const url: string = `${this.http.configService.getCoreApiBaseUrl()}/nodes/external-associations/arena`;
        return url;
    }

    private getCoreApiExternalAssociationUrl(eventId: string): string {
        const url: string = `${this.http.configService.getCoreApiBaseUrl()}/nodes/${eventId}/external-association`;

        return url;
    }
}

export interface ICoreEventExternalAssociation {
    coreEvents: EventCore[];
    externalAssociations: ExternalAssociation[];
}

export interface IJsonDeleteExtAssociationResponse {
    data: {
        status: boolean;
    }
}
